'use client'

import { AddressIcon } from '@repo/ui/AddressIcon'
import { DxIcon } from '@repo/ui/DxIcon'
import { EmailIcon } from '@repo/ui/EmailIcon'
import { ExpertAreaIcon } from '@repo/ui/ExpertAreaIcon'
import { ExpertTypeIcon } from '@repo/ui/ExpertTypeIcon'
import { FaxIcon } from '@repo/ui/FaxIcon'
import { IdTagIcon } from '@repo/ui/IdTagIcon'
import { MobilePhoneIcon } from '@repo/ui/MobilePhoneIcon'
import { OrganisationIcon } from '@repo/ui/OrganisationIcon'
import { PhoneIcon } from '@repo/ui/PhoneIcon'
import { UrlIcon } from '@repo/ui/UrlIcon'
import { useEffect } from 'react'
import { useLookupsStore } from '@repo/store/useLookupsStore'
import { UserIcon } from '@repo/ui/UserIcon'
import MUIStack from '@mui/material/Stack'
import MUITooltip from '@mui/material/Tooltip'
import MUITypography from '@mui/material/Typography'
import PropTypes from 'prop-types'

export const CustomDataContactsOrganisationsEditState = ({ item, mode = 'contacts' }) => {
  const { expertTypesById, expertAreasById, organisationsById, fetchOrganisationById } = useLookupsStore()

  useEffect(() => {
    const fetchOrganisation = async () => {
      if (item?.organisationId) {
        await fetchOrganisationById({ id: item.organisationId })
      }
    }

    if (item?.organisationId && !organisationsById?.[item.organisationId]) fetchOrganisation()
  }, [item?.organisationId, fetchOrganisationById, organisationsById])

  const {
    contactId,
    organisationId,
    title,
    forename,
    surname,
    name,
    email,
    phoneNo,
    dxNo,
    mobileNo,
    faxNo,
    websiteUrl,
    house,
    area,
    postalTown,
    county,
    postcode
  } = item || {}
  const displayName = [title, forename, surname, name].filter(Boolean).join(' ')
  const address = [house, area, postalTown, county, postcode].filter(Boolean).join('\n')
  const organisation = organisationsById?.[item?.organisationId] || null
  const expertType = expertTypesById && item?.expertTypeId ? expertTypesById[item.expertTypeId] : ''
  const expertArea = expertAreasById && item?.expertiseAreaId ? expertAreasById[item.expertiseAreaId] : ''

  const iconStyles = { width: '20px', height: '20px', color: 'rgba(61, 81, 123, 0.8)' }

  return (
    <>
      {item ? (
        <>
          {mode === 'contacts' ? (
            <CustomDataContactsOrganisationsEditStateRow
              icon={<UserIcon sx={iconStyles} />}
              label={displayName}
              tooltip="Contact"
            />
          ) : (
            <CustomDataContactsOrganisationsEditStateRow
              icon={<OrganisationIcon sx={iconStyles} />}
              label={item?.name}
              tooltip="Organisation"
            />
          )}

          <CustomDataContactsOrganisationsEditStateRow
            icon={<IdTagIcon sx={iconStyles} />}
            label={mode === 'contacts' ? contactId : organisationId}
            tooltip="ID"
          />

          <CustomDataContactsOrganisationsEditStateRow
            icon={<ExpertTypeIcon sx={iconStyles} />}
            label={expertType?.description}
            tooltip="Expert type"
          />
          <CustomDataContactsOrganisationsEditStateRow
            icon={<ExpertAreaIcon sx={iconStyles} />}
            label={expertArea?.description}
            tooltip="Expertise area"
          />
          {mode === 'contacts' ? (
            <CustomDataContactsOrganisationsEditStateRow
              icon={<OrganisationIcon sx={iconStyles} />}
              label={organisation?.name}
              tooltip="Organisation"
            />
          ) : null}
          <CustomDataContactsOrganisationsEditStateRow
            icon={<EmailIcon sx={iconStyles} />}
            label={`${email}`}
            tooltip="Email"
          />
          <CustomDataContactsOrganisationsEditStateRow
            icon={<PhoneIcon sx={iconStyles} />}
            label={`${phoneNo}`}
            tooltip="Phone"
          />
          <CustomDataContactsOrganisationsEditStateRow
            icon={<DxIcon sx={iconStyles} />}
            label={`${dxNo}`}
            tooltip="DX"
          />
          <CustomDataContactsOrganisationsEditStateRow
            icon={<MobilePhoneIcon sx={iconStyles} />}
            label={`${mobileNo}`}
            tooltip="Mobile"
          />
          <CustomDataContactsOrganisationsEditStateRow
            icon={<FaxIcon sx={iconStyles} />}
            label={`${faxNo}`}
            tooltip="Fax"
          />
          <CustomDataContactsOrganisationsEditStateRow
            icon={<UrlIcon sx={iconStyles} />}
            label={`${websiteUrl}`}
            tooltip="Website"
          />
          <CustomDataContactsOrganisationsEditStateRow
            icon={<AddressIcon sx={{ ...iconStyles, opacity: 0.8 }} />}
            label={`${address}`}
            tooltip="Address"
            isLast
          />
        </>
      ) : (
        <CustomDataContactsOrganisationsEditStateRow
          icon={mode === 'contacts' ? <UserIcon sx={iconStyles} /> : <OrganisationIcon sx={iconStyles} />}
          label={mode === 'contacts' ? 'No contact selected' : 'No organisation selected'}
          isFirst
          isLast
        />
      )}
    </>
  )
}

CustomDataContactsOrganisationsEditState.propTypes = {
  item: PropTypes.object,
  mode: PropTypes.oneOf(['contacts', 'organisations'])
}

export const CustomDataContactsOrganisationsEditStateRow = ({
  icon,
  label,
  isFirst = false,
  isLast = false,
  tooltip = '',
  title = '',
  sx = {}
}) => {
  if (!label || label === 'undefined' || label === 'null') return null

  return (
    <MUIStack
      direction="row"
      alignItems="center"
      justifyContent="flex-start"
      sx={{
        backgroundColor: icon ? 'white' : 'background.dark',
        minHeight: '36px',
        borderTopLeftRadius: isFirst ? '4px' : '0px',
        borderTopRightRadius: isFirst ? '4px' : '0px',
        borderBottomLeftRadius: isLast ? '4px' : '0px',
        borderBottomRightRadius: isLast ? '4px' : '0px',
        borderBottom: isLast ? 'none' : '1px solid rgb(0, 0, 0, 0.12)',
        ...sx
      }}
    >
      {icon ? (
        <MUITooltip title={tooltip} placement="top" arrow>
          <MUIStack
            direction="row"
            alignItems="center"
            justifyContent="center"
            sx={{
              backgroundColor: 'rgba(61, 81, 123, 0.04)',
              minWidth: '40px',
              maxWidth: '40px',
              cursor: tooltip ? 'pointer' : 'default',
              width: '40px',
              height: '100%',
              borderTopLeftRadius: isFirst ? '4px' : '0px',
              borderBottomLeftRadius: isLast ? '4px' : '0px',
              borderRight: '1px solid rgb(0, 0, 0, 0.23)'
            }}
          >
            {icon}
          </MUIStack>
        </MUITooltip>
      ) : (
        <MUITypography variant="h6" sx={{ mb: 0, p: 2, color: 'text.primary', textTransform: 'uppercase' }}>
          {title}
        </MUITypography>
      )}

      {label && typeof label === 'object' ? (
        label
      ) : (
        <MUITypography
          variant="body1"
          sx={{ mb: 0, p: 2, whiteSpace: 'pre-wrap', overflow: 'hidden', wordBreak: 'break-word' }}
        >
          {label}
        </MUITypography>
      )}
    </MUIStack>
  )
}

CustomDataContactsOrganisationsEditStateRow.propTypes = {
  icon: PropTypes.node,
  label: PropTypes.any,
  isFirst: PropTypes.bool,
  isLast: PropTypes.bool,
  tooltip: PropTypes.string,
  title: PropTypes.string,
  sx: PropTypes.object
}
